import React, { useCallback, useEffect, useRef, useState } from 'react';

import { RequiredAsterisk } from 'components/ui/RequiredAsterisk';

import { Fonts, spacing } from 'styles/theme';
import { Bumper, Label, SubLabel } from 'styles/shared';
import {
    SliderWrapper,
    SliderContainer,
    SliderTrack,
    SliderHandle,
    SliderTicks,
    SliderTick,
    SliderLabels,
    SliderLabel,
} from 'styles/slider';

interface SliderProps {
    label?: string;
    subLabel?: string;
    required?: boolean;
    $isAltText?: boolean;
    value?: number | string;
    min?: number;
    max?: number;
    startLabel?: string;
    endLabel?: string;
    values?: Array<string | number>;
    onChange?: (value: number | string) => void;
    onInput?: (event: React.FormEvent<HTMLSelectElement>) => void;
    name?: string;
}

export const Slider = (props: SliderProps) => {
    const {
        label,
        required,
        subLabel,
        $isAltText,
        value,
        min: minProp,
        max: maxProp,
        startLabel: startLabelProp,
        endLabel: endLabelProp,
        values,
        onChange,
        onInput,
        name,
    } = props;

    // Determine min/max based on values array or props
    const min = values ? 1 : minProp ?? 1;
    const max = values ? values.length : maxProp ?? 10;

    // Determine labels based on values array or props
    const startLabel =
        startLabelProp ?? (values ? String(values[0]) : undefined);
    const endLabel =
        endLabelProp ??
        (values ? String(values[values.length - 1]) : undefined);

    const [isActive, setIsActive] = useState(false);
    const [currentIndex, setCurrentIndex] = useState<number>(() => {
        if (value != null && value !== '') {
            if (values) {
                // Find the index of the value in the array
                const index = values.findIndex(
                    (v) => String(v) === String(value)
                );
                return index >= 0 ? index + 1 : min;
            }
            return Number(value);
        }
        return min;
    });
    const sliderRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);
    const currentIndexRef = useRef<number>(currentIndex);

    // Update currentIndex when value prop changes
    useEffect(() => {
        if (value != null && value !== '') {
            if (values) {
                const index = values.findIndex(
                    (v) => String(v) === String(value)
                );
                const newIndex = index >= 0 ? index + 1 : min;
                setCurrentIndex(newIndex);
                currentIndexRef.current = newIndex;
            } else {
                const newIndex = Number(value);
                setCurrentIndex(newIndex);
                currentIndexRef.current = newIndex;
            }
        }
    }, [value, values, min]);

    const getValueFromPosition = useCallback(
        (clientX: number): number => {
            if (!sliderRef.current) return currentIndex;

            const rect = sliderRef.current.getBoundingClientRect();
            const x = clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, x / rect.width));

            // Calculate the value based on percentage
            const rawValue = min + percentage * (max - min);
            // Round to nearest integer
            const roundedValue = Math.round(rawValue);
            // Clamp to min/max
            return Math.max(min, Math.min(max, roundedValue));
        },
        [min, max, currentIndex]
    );

    const handleStart = useCallback(
        (clientX: number) => {
            setIsActive(true);
            isDraggingRef.current = true;
            const newIndex = getValueFromPosition(clientX);
            setCurrentIndex(newIndex);
            currentIndexRef.current = newIndex;
        },
        [getValueFromPosition]
    );

    const handleMove = useCallback(
        (clientX: number) => {
            if (!isDraggingRef.current) return;
            const newIndex = getValueFromPosition(clientX);
            setCurrentIndex(newIndex);
            currentIndexRef.current = newIndex;
        },
        [getValueFromPosition]
    );

    const handleEnd = useCallback(() => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        setIsActive(false);

        // Use ref to get the latest index value (state might be stale)
        const finalIndex = currentIndexRef.current;

        // Get the actual value (from array if values prop is used, otherwise the index)
        const actualValue: string | number = values
            ? values[finalIndex - 1] ?? values[0] ?? '' // Convert 1-based index to 0-based array index, fallback to first
            : finalIndex;

        // Call onInput if provided (for compatibility with existing Select usage)
        if (onInput) {
            const syntheticEvent = {
                target: {
                    value: String(actualValue),
                    name: name ?? '',
                },
            } as unknown as React.FormEvent<HTMLSelectElement>;
            onInput(syntheticEvent);
        }

        // Call onChange if provided (alternative callback)
        if (onChange) {
            onChange(actualValue);
        }
    }, [values, onInput, onChange, name]);

    // Mouse events
    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            handleStart(e.clientX);
        },
        [handleStart]
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            handleMove(e.clientX);
        },
        [handleMove]
    );

    const handleMouseUp = useCallback(() => {
        handleEnd();
    }, [handleEnd]);

    // Touch events
    const handleTouchStart = useCallback(
        (e: React.TouchEvent<HTMLDivElement>) => {
            e.preventDefault();
            const touch = e.touches[0];
            if (touch) {
                handleStart(touch.clientX);
            }
        },
        [handleStart]
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!isDraggingRef.current) return;
            e.preventDefault();
            const touch = e.touches[0];
            if (touch) {
                handleMove(touch.clientX);
            }
        },
        [handleMove]
    );

    const handleTouchEnd = useCallback(() => {
        handleEnd();
    }, [handleEnd]);

    // Set up global event listeners for dragging
    useEffect(() => {
        if (isActive) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove, {
                passive: false,
            });
            document.addEventListener('touchend', handleTouchEnd);
            document.addEventListener('touchcancel', handleTouchEnd);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
                document.removeEventListener('touchcancel', handleTouchEnd);
            };
        }
    }, [
        isActive,
        handleMouseMove,
        handleMouseUp,
        handleTouchMove,
        handleTouchEnd,
    ]);

    // Calculate position percentage
    const position = ((currentIndex - min) / (max - min)) * 100;

    // Generate ticks
    const ticks = [];
    for (let i = min; i <= max; i++) {
        ticks.push(i);
    }

    // Get display value for handle
    const displayValue = values
        ? values[currentIndex - 1] ?? values[0] // Convert 1-based index to 0-based array index, fallback to first
        : currentIndex;

    return (
        <SliderWrapper>
            <span>
                {$isAltText ? (
                    <>
                        {label == null ? null : (
                            <Fonts.Medium $isOpenSansSemiBold>
                                {label}
                            </Fonts.Medium>
                        )}
                        {subLabel == null ? null : (
                            <Fonts.Medium>
                                {subLabel}
                                {required ? <RequiredAsterisk /> : null}
                            </Fonts.Medium>
                        )}
                        <Bumper $minHeight={spacing.xxSmall} />
                    </>
                ) : (
                    <>
                        {label == null ? null : (
                            <Label>
                                {label}
                                {required ? <RequiredAsterisk /> : null}
                            </Label>
                        )}
                        {subLabel == null ? null : (
                            <SubLabel>{subLabel}</SubLabel>
                        )}
                    </>
                )}
                {values == null || value == null ? null : (
                    <span>
                        <Fonts.Small>&nbsp;{`  Your selection: `}</Fonts.Small>
                        <Fonts.MediumQuote>{value}</Fonts.MediumQuote>
                    </span>
                )}
            </span>
            <SliderContainer>
                <SliderTrack
                    ref={sliderRef}
                    $progress={position}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                    <SliderTicks>
                        {ticks.map((tick) => (
                            <SliderTick
                                key={tick}
                                $isActive={tick === currentIndex}
                            />
                        ))}
                    </SliderTicks>
                    <SliderHandle $position={position} $isActive={isActive}>
                        <Fonts.XSmall>
                            {(value == null && !isActive) || values != null
                                ? ''
                                : displayValue}
                        </Fonts.XSmall>
                    </SliderHandle>
                </SliderTrack>
            </SliderContainer>
            {(startLabel || endLabel) && (
                <SliderLabels>
                    {startLabel && <SliderLabel>{startLabel}</SliderLabel>}
                    {endLabel && <SliderLabel>{endLabel}</SliderLabel>}
                </SliderLabels>
            )}
        </SliderWrapper>
    );
};
